import { useState } from "react";
import { ExternalLink } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { Badge } from "@/components/ui/badge";

import { Card, CardContent, CardHeader } from "@/components/ui/card";

import { cn } from "@/lib/utils";

type Sentiment = "positive" | "negative" | "neutral" | "unknown";

export type FeedCardProps = {
  source: string;
  profileLink?: string;
  content: string;
  platform?: string;
  tags: string[];
  sentiment: Sentiment;
  engagements: string;
};

const sentimentStyles: Record<Sentiment, { border: string; pill: string }> = {
  positive: {
    border: "border-l-success",
    pill: "bg-success/10 text-success",
  },
  negative: {
    border: " border-l-destructive",
    pill: "bg-destructive/10 text-destructive",
  },
  neutral: {
    border: " border-l-warning",
    pill: "bg-warning/10 text-warning",
  },
  unknown: {
    border: "border-l-muted-foreground",
    pill: "bg-muted/10 text-muted-foreground",
  },
};

export function FeedCard({ source, profileLink, content, platform, tags, sentiment, engagements }: FeedCardProps) {
  const { border, pill } = sentimentStyles[sentiment];
  const [expanded, setExpanded] = useState(false);
  const shouldClamp = content.length > 220;

  return (
    <Card className={cn("overflow-hidden rounded-xl border border-border border-l-4 bg-card shadow-none gap-0 py-0", border)}>
      <CardHeader className="flex justify-between pb-2 px-4 pt-4">
        <div className="flex min-w-0 items-center gap-2">
          <p className="text-sm font-semibold text-foreground truncate">{source}</p>
          {profileLink ? (
            <Link to={profileLink} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground shrink-0" aria-label="Open profile">
              <ExternalLink className="size-3.5" />
            </Link>
          ) : null}
        </div>
        <Badge className={cn("rounded-md capitalize shrink-0", pill)}>{sentiment}</Badge>
      </CardHeader>

      <CardContent className="px-4 pb-4 pt-0">
        <p className={cn("text-sm text-foreground/80 leading-relaxed whitespace-pre-line", !expanded && "line-clamp-3")}>{content}</p>
        {shouldClamp ? (
          <button type="button" onClick={() => setExpanded((v) => !v)} className="mt-1 text-xs font-medium underline underline-offset-2 ">
            {expanded ? "Read less ↑" : "Read more →"}
          </button>
        ) : null}

        <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
          {platform ? (
            <Badge variant="secondary" className="rounded-full capitalize shrink-0">
              {platform}{" "}
            </Badge>
          ) : null}

          <div className="flex items-center gap-2 ml-auto">
            {tags.map((tag) => (
              <Badge key={tag} variant="secondary" className="rounded-full text-xs capitalize shrink-0">
                {tag}
              </Badge>
            ))}

            <p className="text-xs text-muted-foreground shrink-0">
              <span className="font-medium text-foreground">{engagements}</span> engagements
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
